import { useCallback, useEffect, useState } from "react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { useSEO } from "@/lib/useSEO";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Play, RotateCcw, Share2, Sparkles, Zap, Target, Shield, Smartphone, Timer, Heart, Sliders, Activity, Loader2, Lock, Calendar } from "lucide-react";
import { MODES as STATIC_MODES, modeById, formatTime, primeSpeech } from "@/lib/roninBox";
import { RONIN_LOGO } from "@/lib/brand";
import { BrandLogo } from "@/components/BrandMark";
import { RoninBoxRunner } from "@/components/RoninBoxRunner";
import { RoninBoxShare } from "@/components/RoninBoxShare";
import { RoninAuthGate } from "@/components/RoninAuthGate";
import { RoninCuePicker } from "@/components/RoninCuePicker";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const LS_KEY = "ronin_box_config_v1";
const LS_LEVEL_KEY = "ronin_box_level_v1";

const LEVELS = [
  { id: "beginner",     label: "Beginner",     tagline: "Coach's picks if you're just starting out" },
  { id: "intermediate", label: "Intermediate", tagline: "Coach's picks if you have solid experience" },
  { id: "advanced",     label: "Advanced",     tagline: "Coach's picks for high-level boxers" },
];

const DEFAULTS = {
  mode: "shadow",
  rounds: 3,
  roundSecs: 180,
  restSecs: 60,
  intensity: "medium",
  exCount: 2,   // circuit: exercise breaks per round
  exLen: 30,    // circuit: seconds per exercise break
};

// Map an icon name (returned by the backend) to a Lucide component. Keeping the catalog small
// so the admin can't pick an icon that doesn't render.
const MODE_ICONS = { Sliders, Zap, Target, Shield, Sparkles, Heart, Timer, Smartphone, Activity };
function modeIcon(name) { return MODE_ICONS[name] || Zap; }

export default function RoninBox() {
  const [config, setConfig] = useState(DEFAULTS);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [showShare, setShowShare] = useState(false);
  const [authGateOpen, setAuthGateOpen] = useState(false);
  const [cuePickerOpen, setCuePickerOpen] = useState(false);
  const [selectedCueIds, setSelectedCueIds] = useState(null); // null until picker confirms
  const [hasAccess, setHasAccess] = useState(null); // null=unknown, true/false=resolved
  const [modes, setModes] = useState(STATIC_MODES); // dynamic from API; falls back to static
  const [level, setLevel] = useState("beginner");
  const { user, loading: authLoading } = useAuth();

  // Hydrate level + modes from API on mount
  useEffect(() => {
    try {
      const lv = localStorage.getItem(LS_LEVEL_KEY);
      if (lv && LEVELS.find((l) => l.id === lv)) setLevel(lv);
    } catch { /* ignore */ }
    api.get("/ronin-box/modes").then(({ data }) => {
      if (Array.isArray(data) && data.length > 0) setModes(data);
    }).catch(() => { /* fall back to static MODES */ });
  }, []);

  // Persist level
  useEffect(() => {
    try { localStorage.setItem(LS_LEVEL_KEY, level); } catch { /* ignore */ }
  }, [level]);

  // Resolve WEB trainer access whenever auth state changes. The browser Ronin app is
  // members-only: admins + Better Through Boxing buyers (paid booking). Not advertised publicly.
  const recheckAccess = useCallback(async () => {
    try {
      const { data } = await api.get("/ronin-box/web-access");
      setHasAccess(!!data?.has_access);
      return !!data?.has_access;
    } catch { setHasAccess(false); return false; }
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (authLoading) return;
    if (!user) { setHasAccess(false); return; }
    (async () => { const ok = await recheckAccess(); if (cancelled) setHasAccess(ok); })();
    return () => { cancelled = true; };
  }, [user, authLoading, recheckAccess]);

  useSEO({
    title: "Ronin Box — Mobile Boxing Training Partner | Better Through Boxing",
    description: "Free voice-cued boxing workouts: shadow boxing, bag work, and defense mode with coach-corner cues, custom rounds, and shareable workout cards. Train anywhere.",
    path: "/ronin-box",
    keywords: [
      "boxing workout app", "shadow boxing trainer", "boxing cues", "bag work timer",
      "voice cued boxing", "defense mode boxing", "Better Through Boxing", "Ronin Box",
    ],
  });

  // Hydrate config from localStorage once on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) {
        const saved = JSON.parse(raw);
        setConfig({ ...DEFAULTS, ...saved });
      }
    } catch { /* ignore */ }
  }, []);

  // Persist on change
  useEffect(() => {
    try { localStorage.setItem(LS_KEY, JSON.stringify(config)); } catch { /* ignore */ }
  }, [config]);

  const start = () => {
    if (!hasAccess) {
      setAuthGateOpen(true);
      return;
    }
    // Open cue picker for EVERY mode — user picks level + customizes before workout starts.
    setCuePickerOpen(true);
  };

  const runWorkout = () => {
    primeSpeech(); // Required user-gesture for Web Speech API
    setResult(null);
    setRunning(true);
  };

  const onCuePickerConfirm = (ids) => {
    setSelectedCueIds(ids);
    setCuePickerOpen(false);
    runWorkout();
  };

  const onComplete = (r) => {
    setResult(r);
    setRunning(false);
  };

  if (running) {
    return <RoninBoxRunner config={config} selectedCueIds={selectedCueIds} selectedLevel={level} onExit={() => setRunning(false)} onComplete={onComplete} />;
  }

  // Web trainer is members-only + unlisted. Show a gate until access is confirmed.
  if (authLoading || hasAccess === null) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex flex-col" data-testid="ronin-page-loading">
        <SiteHeader />
        <div className="flex-1 flex items-center justify-center text-zinc-500">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
        <SiteFooter />
      </div>
    );
  }
  if (!hasAccess) {
    return (
      <RoninMembersOnly
        user={user}
        onSignIn={() => setAuthGateOpen(true)}
        authGate={
          <RoninAuthGate
            open={authGateOpen}
            onClose={() => setAuthGateOpen(false)}
            onAccessGranted={async () => { setAuthGateOpen(false); await recheckAccess(); }}
          />
        }
      />
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="ronin-page">
      <SiteHeader />

      {/* Hero */}
      <section className="relative border-b border-[#27272A] bg-[#0A0A0A] overflow-hidden">
        <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{ background: "radial-gradient(circle at 80% 20%, #FF331F 0%, transparent 50%)" }} />
        <div className="relative max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-24 grid md:grid-cols-5 gap-10 items-center">
          <div className="md:col-span-3">
            <div className="flex items-center gap-5 mb-7" data-testid="ronin-logos">
              <img src={RONIN_LOGO} alt="Ronin Box logo" className="h-28 w-28 sm:h-32 sm:w-32 object-contain rounded-sm" />
              <div className="flex flex-col gap-1 border-l border-[#27272A] pl-5">
                <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-bold">A program by</span>
                <BrandLogo className="h-9 w-auto object-contain" />
                <span className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold">Better Through Boxing</span>
              </div>
            </div>
            <p className="text-[11px] uppercase tracking-[0.3em] text-[#FF331F] font-bold mb-3 inline-flex items-center gap-2">
              <Sparkles className="h-3 w-3" /> Better Through Boxing · Members
            </p>
            <h1 className="font-display text-5xl sm:text-6xl md:text-7xl text-white uppercase tracking-tight leading-[0.9]" data-testid="ronin-h1">
              Ronin Box.<br /><span className="text-[#FF331F]">Your mobile</span><br />training partner.
            </h1>
            <p className="mt-6 max-w-xl text-base md:text-lg text-zinc-400 leading-relaxed">
              Voice-cued boxing workouts that travel with you. Shadow box in the bedroom, work the bag in the garage, or sharpen your reactions with defense mode.
            </p>
            {!result && (
              <div className="mt-8 flex flex-wrap gap-3">
                <Button onClick={start} className="bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-[0.2em] text-xs px-8 py-6 rounded-sm" data-testid="ronin-quick-start">
                  <Play className="h-4 w-4 mr-2" /> Start workout
                </Button>
                <a href="#setup" className="inline-flex items-center justify-center border border-[#27272A] bg-[#0A0A0A] hover:bg-[#1a1a1a] text-zinc-300 font-bold uppercase tracking-[0.2em] text-xs px-6 py-6 rounded-sm">Customize first</a>
              </div>
            )}
          </div>
          <div className="md:col-span-2">
            <div className="border border-[#27272A] bg-[#050505] p-6 rounded-sm">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold mb-4">What you get</p>
              <FeatureRow icon={Target} title="3 training modes" sub="Shadow · Bag · Defense" />
              <FeatureRow icon={Zap} title="Real coach cues" sub="Light jab, stick him, slip outside…" />
              <FeatureRow icon={Timer} title="Fully configurable" sub="Rounds, rest, custom timing" />
              <FeatureRow icon={Share2} title="Share your work" sub="Auto-generates IG-ready stat cards" />
              <FeatureRow icon={Smartphone} title="Mobile-first" sub="Works on any phone, no install" />
            </div>
          </div>
        </div>
      </section>

      {/* Results card (after a workout) */}
      {result && (
        <section className="max-w-3xl mx-auto px-6 md:px-10 py-12">
          <div className="border border-[#FF331F]/40 bg-[#0A0A0A] rounded-sm overflow-hidden" data-testid="ronin-summary">
            <div className="px-6 py-4 border-b border-[#27272A] bg-[#FF331F]/5">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold">{result.aborted ? "Workout ended" : "Workout complete"}</p>
              <h2 className="font-display text-3xl text-white uppercase tracking-tight mt-1">{modeById(result.mode).label}</h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-[#27272A]">
              <SummaryStat label="Active time" value={formatTime(result.activeSec)} />
              <SummaryStat label="Rounds" value={`${result.roundsCompleted}/${result.rounds}`} />
              <SummaryStat label="Punches" value={result.punches} />
              <SummaryStat label="Calories" value={result.calories} />
            </div>
            <div className="p-6 grid sm:grid-cols-3 gap-2">
              <Button onClick={() => setShowShare(true)} className="bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest text-xs py-5 rounded-sm" data-testid="ronin-share-btn">
                <Share2 className="h-4 w-4 mr-2" /> Share workout
              </Button>
              <Button onClick={start} variant="outline" className="border-[#27272A] bg-[#0A0A0A] text-zinc-200 hover:bg-[#1a1a1a] font-bold uppercase tracking-widest text-xs py-5 rounded-sm" data-testid="ronin-repeat-btn">
                <RotateCcw className="h-4 w-4 mr-2" /> Run it back
              </Button>
              <a href="#setup" className="inline-flex items-center justify-center border border-[#27272A] bg-[#0A0A0A] hover:bg-[#1a1a1a] text-zinc-300 font-bold uppercase tracking-widest text-xs py-5 rounded-sm">
                Change settings
              </a>
            </div>
          </div>
        </section>
      )}

      {/* Setup */}
      <section id="setup" className="max-w-5xl mx-auto px-6 md:px-10 py-12 md:py-16">
        <div className="mb-8">
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold mb-2">Customize</p>
          <h2 className="font-display text-3xl sm:text-4xl text-white uppercase tracking-tight">Build your round.</h2>
          <p className="text-sm text-zinc-400 mt-2 max-w-2xl">Settings save to your device automatically.</p>
        </div>

        {/* Mode */}
        <div className="mb-10">
          <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-3 block">Mode</Label>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {modes.map((m) => {
              const Icon = modeIcon(m.icon);
              const active = config.mode === m.id;
              return (
                <button
                  key={m.id}
                  onClick={() => setConfig({ ...config, mode: m.id })}
                  className={`text-left p-5 border rounded-sm transition-all ${active ? "border-[#FF331F] bg-[#FF331F]/5 ring-1 ring-[#FF331F]/30" : "border-[#27272A] bg-[#0A0A0A] hover:border-zinc-700"}`}
                  data-testid={`ronin-mode-${m.id}`}
                >
                  <Icon className={`h-5 w-5 mb-3 ${active ? "text-[#FF331F]" : "text-zinc-500"}`} />
                  <div className="font-display text-xl text-white uppercase tracking-tight mb-1">{m.label}</div>
                  <div className="text-[10px] uppercase tracking-widest text-[#FF331F] font-bold mb-2">{m.tagline}</div>
                  <div className="text-xs text-zinc-400 leading-relaxed">{m.description}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Level selector — shown for every mode so the user picks their tier before
            customizing in the picker. Default and persistence handled via localStorage. */}
        <div className="mb-10" data-testid="ronin-level-section">
          <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-1 block">Level</Label>
          <p className="text-xs text-zinc-500 mb-3">
            Pick the tier that fits your experience — Coach Chris curated each one. You&apos;ll be able to add or remove individual cues on the next screen.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {LEVELS.map((l) => {
              const active = level === l.id;
              return (
                <button
                  key={l.id}
                  type="button"
                  onClick={() => setLevel(l.id)}
                  className={`text-left p-3 border rounded-sm transition-all ${active ? "border-[#FF331F] bg-[#FF331F]/5 ring-1 ring-[#FF331F]/30" : "border-[#27272A] bg-[#0A0A0A] hover:border-zinc-700"}`}
                  data-testid={`ronin-level-${l.id}`}
                >
                  <div className="font-display text-base uppercase tracking-tight text-white">{l.label}</div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mt-1">{l.tagline}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Numerics */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          <NumberStepper
            label="Rounds" value={config.rounds} min={1} max={12} step={1}
            onChange={(v) => setConfig({ ...config, rounds: v })}
            format={(v) => `${v}`}
            testid="ronin-rounds"
          />
          <NumberStepper
            label="Round time" value={config.roundSecs} min={30} max={300} step={30}
            onChange={(v) => setConfig({ ...config, roundSecs: v })}
            format={(v) => formatTime(v)}
            testid="ronin-roundsecs"
          />
          <NumberStepper
            label="Rest time" value={config.restSecs} min={15} max={120} step={15}
            onChange={(v) => setConfig({ ...config, restSecs: v })}
            format={(v) => formatTime(v)}
            testid="ronin-restsecs"
          />
        </div>

        {config.mode === "circuit" && (
          <div className="grid sm:grid-cols-2 gap-6 mb-10 border border-[#27272A] bg-[#0A0A0A] p-6 rounded-sm" data-testid="circuit-config">
            <div className="sm:col-span-2 flex items-center gap-2 text-[#FF331F]">
              <Activity className="h-4 w-4" />
              <p className="text-[10px] uppercase tracking-[0.3em] font-bold">Circuit — exercise breaks</p>
            </div>
            <NumberStepper
              label="Breaks / round" value={config.exCount} min={1} max={5} step={1}
              onChange={(v) => setConfig({ ...config, exCount: v })}
              format={(v) => `${v}`}
              testid="ronin-excount"
            />
            <NumberStepper
              label="Break length" value={config.exLen} min={15} max={90} step={15}
              onChange={(v) => setConfig({ ...config, exLen: v })}
              format={(v) => formatTime(v)}
              testid="ronin-exlen"
            />
            <p className="sm:col-span-2 text-xs text-zinc-500">Shadowbox, then drop into a random exercise (burpees, push-ups…) for each break — announced in the coach voice. Manage the exercise pool in the admin.</p>
          </div>
        )}

        {/* Intensity picker removed — cue timing is now controlled entirely by the
            backend's per-cue `cue_seconds` / `punches` / `movement_count` overrides
            in /admin/ronin-box/timing. Runner still passes intensity="medium" so the
            multiplier stays 1.0 (a no-op) and admin timings fire exactly as authored. */}

        {/* Summary + start */}
        <div className="border border-[#27272A] bg-[#0A0A0A] p-6 rounded-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1">Your round</p>
            <p className="font-display text-xl text-white uppercase tracking-tight">
              {modeById(config.mode).label} · {config.rounds} × {formatTime(config.roundSecs)} · {formatTime(config.restSecs)} rest
            </p>
            <p className="text-xs text-zinc-500 mt-1">Total: ~{formatTime(config.rounds * config.roundSecs + (config.rounds - 1) * config.restSecs)} active</p>
          </div>
          <Button onClick={start} className="bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-[0.2em] text-xs px-8 py-6 rounded-sm" data-testid="ronin-start-btn">
            <Play className="h-4 w-4 mr-2" /> Start workout
          </Button>
        </div>
      </section>

      {/* How-it-works strip */}
      <section className="border-t border-[#27272A] bg-[#0A0A0A]">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-14 grid md:grid-cols-3 gap-10">
          <Step n="01" title="Press start" body="Your phone speaks the cues out loud — turn the volume up, throw your hands. Works on any modern browser." />
          <Step n="02" title="Move with the corner" body="Hear coach-corner cues like &quot;stick him,&quot; &quot;slip outside,&quot; &quot;pivot left.&quot; In defense mode, react to incoming shots." />
          <Step n="03" title="Share the work" body="Workout card auto-generates with your stats — drop your post-workout selfie behind it and post." />
        </div>
      </section>

      {/* Roadmap nudge */}
      <section className="max-w-5xl mx-auto px-6 md:px-10 py-14">
        <div className="border border-dashed border-[#27272A] rounded-sm p-6 sm:p-8 flex items-start gap-4">
          <Heart className="h-6 w-6 text-[#FF331F] mt-1 shrink-0" />
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold mb-1">Coming soon</p>
            <p className="text-zinc-300 leading-relaxed">
              Native iOS + Android apps with Apple Health / Fitbit integration, premium ElevenLabs trainer voice, workout history, and watch-face cues. Want early access? <a href="/library" className="text-[#FF331F] underline font-bold">Join the newsletter</a> — we&apos;ll notify you the day they ship.
            </p>
          </div>
        </div>
      </section>

      <SiteFooter />

      {showShare && result && <RoninBoxShare result={result} onClose={() => setShowShare(false)} />}
      <RoninAuthGate
        open={authGateOpen}
        onClose={() => setAuthGateOpen(false)}
        onAccessGranted={async () => {
          setAuthGateOpen(false);
          // Signing in does NOT grant web access — re-check the paid-booking entitlement.
          const ok = await recheckAccess();
          if (ok) setCuePickerOpen(true);
        }}
      />
      <RoninCuePicker
        open={cuePickerOpen}
        mode={config.mode === "circuit" ? "shadow" : config.mode}
        initialLevel={level}
        onConfirm={onCuePickerConfirm}
        onCancel={() => setCuePickerOpen(false)}
      />
    </div>
  );
}

// Members-only gate for the unlisted browser Ronin trainer. Two states: signed-out
// (prompt sign-in) and signed-in-without-access (Better Through Boxing upsell).
function RoninMembersOnly({ user, onSignIn, authGate }) {
  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col" data-testid="ronin-members-only">
      <SiteHeader />
      <main className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="max-w-lg w-full text-center border border-[#27272A] bg-[#0A0A0A] rounded-lg p-8 sm:p-10">
          <img src={RONIN_LOGO} alt="Ronin Box" className="h-20 w-20 object-contain mx-auto mb-6 rounded-sm" />
          <div className="inline-flex items-center gap-2 text-[#FF331F] text-[10px] uppercase tracking-[0.3em] font-bold mb-3">
            <Lock className="h-3.5 w-3.5" /> Members only
          </div>
          <h1 className="font-display text-3xl sm:text-4xl uppercase tracking-tight">Web Trainer</h1>
          {!user ? (
            <>
              <p className="mt-4 text-zinc-400">The browser trainer is included with the <span className="text-white font-bold">Better Through Boxing</span> program. Sign in to launch it.</p>
              <Button onClick={onSignIn} className="mt-7 bg-[#FF331F] hover:bg-[#FF331F]/85 text-white font-bold uppercase tracking-widest text-xs px-6 py-5" data-testid="members-signin">
                Sign in
              </Button>
            </>
          ) : (
            <>
              <p className="mt-4 text-zinc-400">The browser trainer is included with the <span className="text-white font-bold">Better Through Boxing</span> program. Book a session to unlock it — or train anywhere with the mobile app.</p>
              <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
                <a href="/book" data-testid="members-book">
                  <Button className="w-full bg-[#FF331F] hover:bg-[#FF331F]/85 text-white font-bold uppercase tracking-widest text-xs px-6 py-5">
                    <Calendar className="h-4 w-4 mr-2" /> Book a session
                  </Button>
                </a>
                <a href="/ronin-box" data-testid="members-get-app">
                  <Button variant="outline" className="w-full border-[#27272A] bg-transparent text-zinc-200 hover:text-white font-bold uppercase tracking-widest text-xs px-6 py-5">
                    <Smartphone className="h-4 w-4 mr-2" /> Get the app
                  </Button>
                </a>
              </div>
              <p className="mt-5 text-[11px] text-zinc-600">Signed in as {user.email}. Already booked? Your access unlocks automatically once payment clears.</p>
            </>
          )}
        </div>
      </main>
      <SiteFooter />
      {authGate}
    </div>
  );
}


function FeatureRow({ icon: Icon, title, sub }) {
  return (
    <div className="flex items-start gap-3 py-2">
      <Icon className="h-4 w-4 text-[#FF331F] mt-0.5 shrink-0" />
      <div>
        <div className="text-sm text-white font-bold">{title}</div>
        <div className="text-xs text-zinc-500">{sub}</div>
      </div>
    </div>
  );
}

function SummaryStat({ label, value }) {
  return (
    <div className="bg-[#0A0A0A] p-5">
      <div className="font-display text-3xl text-white">{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mt-1">{label}</div>
    </div>
  );
}

function Step({ n, title, body }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.3em] text-[#FF331F] font-bold mb-3">{n}</div>
      <h3 className="font-display text-2xl text-white uppercase tracking-tight mb-2">{title}</h3>
      <p className="text-sm text-zinc-400 leading-relaxed">{body}</p>
    </div>
  );
}

function NumberStepper({ label, value, min, max, step, onChange, format, testid }) {
  const dec = () => onChange(Math.max(min, value - step));
  const inc = () => onChange(Math.min(max, value + step));
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-3 block">{label}</Label>
      <div className="flex items-stretch border border-[#27272A] rounded-sm overflow-hidden">
        <button onClick={dec} disabled={value <= min} className="px-4 bg-[#0A0A0A] hover:bg-[#1a1a1a] disabled:opacity-30 text-xl text-zinc-300" data-testid={`${testid}-dec`}>−</button>
        <div className="flex-1 bg-[#050505] flex items-center justify-center py-3" data-testid={testid}>
          <span className="font-display text-3xl text-white">{format(value)}</span>
        </div>
        <button onClick={inc} disabled={value >= max} className="px-4 bg-[#0A0A0A] hover:bg-[#1a1a1a] disabled:opacity-30 text-xl text-zinc-300" data-testid={`${testid}-inc`}>+</button>
      </div>
    </div>
  );
}
